import Styles from './BrandProfileWithEditing.module.scss'
import { Formik } from 'formik'
import { BrandProfileProps } from 'components/FirstTimeExperienceModal/steps/BrandProfile/Types'

import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { StickyFooter, FooterButtons } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight, Edit1 } from 'blaze-ui/icons'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Data } from 'hooks/queries/brandProfiles/useBrandProfileAnalysis'
import { AIJobStatus } from 'types'
import { usePollBrandProfileAnalysis } from 'components/FirstTimeExperienceModal/steps/BrandProfile/usePollBrandProfileAnalysis'
import { fetchStream } from 'common/Prose/nodeViews/AISuggestion/utils'
import { LoadingProfile } from 'components/FirstTimeExperienceModal/steps/BrandProfile/LoadingProfile'
import { reportError } from 'utils/error'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useDeleteBrandProfile } from 'hooks/queries/brandProfiles/useDeleteBrandProfile'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { LoadingAnimation } from './LoadingAnimation'
import { Heading } from 'blaze-ui/Heading/Heading'
import { makePossessiveSingular } from 'utils'

import { useUpdateBrandProfile } from 'hooks/queries/brandProfiles/useUpdateBrandProfile'
import {
  parseProfileSections,
  reconstructMarkdown,
  ProfileSection,
} from 'screens/BrandKits/components/BrandProfile/markdownUtils'
import { EditableSection } from './EditableSection'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { LoadingText } from './LoadingText'

const BrandProfileWithEditing = ({
  brandProfile,
  onNext,
  onBack,
  analysisId,
  initialProfile,
  deleteOnBack = true,
}: BrandProfileProps) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const [profile, setProfile] = useState<string>(() => {
    if (initialProfile) return initialProfile
    if (brandProfile.profile instanceof Object) return ''
    return `${brandProfile.profile}`
  })
  const [refreshProfile, setRefreshProfile] = useState(false)
  const [analysisComplete, setAnalysisComplete] = useState(false)

  const updateMutation = useUpdateBrandProfile({ id: brandProfile.id })
  const { trackBrandProfileEdited } = useOnboardingTracking()

  const [sections, setSections] = useState<ProfileSection[]>([])
  const [editingSectionTitle, setEditingSectionTitle] = useState<string | null>(null)

  const { data: brandKitData } = useBrandKit(brandProfile.brand_kit.id, { cacheTime: 1000 * 60 * 5 })
  const { mutate: deleteBrandProfile } = useDeleteBrandProfile({ id: brandProfile.id })
  const { mutate: deleteBrandKitSource } = useDeleteBrandKitSource()

  const handleBack = useCallback(
    async (websiteScrapeSucceeded = true) => {
      setProfile('')
      if (!deleteOnBack) {
        onBack(websiteScrapeSucceeded)
        return
      }
      deleteBrandProfile()
      brandKitData?.brand_kit?.brand_kit_sources?.forEach((source) =>
        deleteBrandKitSource({ brandKitId: brandProfile.brand_kit.id, id: source.id }),
      )
      onBack(websiteScrapeSucceeded)
    },
    [
      deleteOnBack,
      deleteBrandProfile,
      brandProfile.brand_kit.id,
      brandKitData?.brand_kit?.brand_kit_sources,
      deleteBrandKitSource,
      onBack,
    ],
  )

  const topRef = useRef<HTMLDivElement | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  const handleAnalysisComplete = useCallback(
    async (data: Data) => {
      const { status } = data.brand_profile_analysis
      if (status === AIJobStatus.Successful) {
        await queryClient.refetchQueries(['workspaces', workspaceId, 'brandProfiles', 'detail', brandProfile.id])
        setRefreshProfile(true)
        setAnalysisComplete(true)
        return
      }

      if (status === AIJobStatus.Failed || status === AIJobStatus.Expired) {
        handleBack(false)
      }
    },
    [queryClient, workspaceId, brandProfile.id, handleBack],
  )

  const handleSectionUpdate = useCallback(
    async (sectionTitle: string, newContent: string) => {
      if (!brandProfile?.id) return

      const updatedSections = sections.map((section) =>
        section.title === sectionTitle ? { ...section, content: newContent } : section,
      )

      const originalSections = sections
      setSections(updatedSections)

      const fullMarkdown = reconstructMarkdown(updatedSections)

      try {
        const updatePayload = {
          profile: fullMarkdown,
        }

        await updateMutation.mutateAsync(updatePayload)
        trackBrandProfileEdited()
        setProfile(fullMarkdown)
      } catch (error) {
        setSections(originalSections)
        reportError(error)
        showErrorMessageToast('Failed to save changes. Please try again.')
      }
    },
    [sections, updateMutation, brandProfile?.id, trackBrandProfileEdited],
  )

  const { data, isLoading } = usePollBrandProfileAnalysis(
    { id: analysisId, brandProfileId: brandProfile.id },
    { onSuccess: handleAnalysisComplete },
  )

  useEffect(() => {
    if (typeof brandProfile.profile === 'string' && brandProfile.profile.length > 0 && profile.length === 0) {
      setProfile(brandProfile.profile)
    }
  }, [brandProfile.profile, profile.length])

  useEffect(() => {
    if (
      refreshProfile &&
      typeof brandProfile.profile === 'string' &&
      brandProfile.profile.length > 0 &&
      brandProfile.profile !== profile
    ) {
      setProfile(brandProfile.profile)
      setRefreshProfile(false)
    }
  }, [brandProfile.profile, refreshProfile, profile])

  useEffect(() => {
    const analysisJobId = data?.brand_profile_analysis?.job_id
    const isAnalysisComplete = data?.brand_profile_analysis?.status === AIJobStatus.Successful

    if (!analysisJobId || isAnalysisComplete) return
    if (brandProfile.profile && typeof brandProfile.profile === 'string' && brandProfile.profile.length > 0) {
      return
    }

    setProfile('')
    fetchStream({
      jobId: analysisJobId,
      onParsedLine: (line: string) => {
        setProfile((stream) => stream + line)
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      },
      onLastLineParsed: () => {
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      },
      onError: (error) => {
        reportError(error)
        onBack(true)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.brand_profile_analysis?.job_id, data?.brand_profile_analysis?.status, brandProfile.profile])

  useEffect(() => {
    if (profile.length > 0) {
      const parsedSections = parseProfileSections(profile)
      setSections(parsedSections)
    } else {
      setSections([])
    }
  }, [profile])

  const analysisStatus = data?.brand_profile_analysis.status
  const completedStates = [AIJobStatus.Successful, AIJobStatus.Failed, AIJobStatus.Expired]
  const hasFinished = analysisStatus && completedStates.includes(analysisStatus)

  const loadingElement = !analysisComplete ? (
    <LoadingText
      url={brandProfile.url}
      handleBack={handleBack}
      cancelOnError={false}
      finishedLoading={profile.length > 1}
    />
  ) : null

  if (hasFinished) {
    return (
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <ResponsiveDesign.Desktop>
                <Button
                  variant="tertiary"
                  size={isOnboardingRedesign ? 'lg' : 'xl'}
                  onPress={() => handleBack()}
                  frontIcon={isOnboardingRedesign ? undefined : ArrowLeft}
                  aria-label="Back"
                >
                  Back
                </Button>
              </ResponsiveDesign.Desktop>
              <Button
                size={isOnboardingRedesign ? 'lg' : 'xl'}
                onPress={() => onNext(profile)}
                isDisabled={
                  data?.brand_profile_analysis.status !== AIJobStatus.Successful || editingSectionTitle !== null
                }
                endIcon={isOnboardingRedesign ? undefined : ArrowRight}
                aria-label="Looks Good"
                fullWidth={isMobile}
              >
                Looks Good
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <>
          {!isOnboardingRedesign && (
            <h2 className={Styles.introHeading}>Here's your brand profile with key information about your business.</h2>
          )}
          <div className={Styles.container}>
            <div className={Styles.topSection}>
              <BrandProfileTitleWithEditing name={brandProfile.name} isSaving={false} loadingElement={loadingElement} />
            </div>
            <div className={Styles.form}>
              <div ref={topRef} />
              {sections.length > 0 && (
                <div className={Styles.brandProfileContent}>
                  {sections.map((section, index) => (
                    <EditableSection
                      key={`${section.title}-${index}`}
                      section={section}
                      onSectionUpdate={handleSectionUpdate}
                      isUpdating={updateMutation.isLoading}
                      isEditing={editingSectionTitle === section.title}
                      onEdit={() => setEditingSectionTitle(section.title)}
                      onCancel={() => setEditingSectionTitle(null)}
                      isDisabled={editingSectionTitle !== null && editingSectionTitle !== section.title}
                      sectionClassName={Styles.section}
                      headerClassName={Styles.sectionHeader}
                      actionsClassName={Styles.sectionActions}
                      editButtonClassName={Styles.editButton}
                      contentClassName={Styles.sectionContent}
                      editingClassName={Styles.editingContainer}
                      editorClassName={Styles.proseEditor}
                    />
                  ))}
                </div>
              )}
              {(isLoading || profile.length < 1) && !isOnboardingRedesign && <LoadingProfile />}
              <div ref={bottomRef} />
            </div>
          </div>
        </>
      </StickyFooterProvider>
    )
  }

  return (
    <StickyFooterProvider
      footer={
        <StickyFooter>
          <FooterButtons>
            <ResponsiveDesign.Desktop>
              <Button
                variant="tertiary"
                size={isOnboardingRedesign ? 'lg' : 'xl'}
                onPress={() => handleBack()}
                frontIcon={isOnboardingRedesign ? undefined : ArrowLeft}
                aria-label="Back"
              >
                Back
              </Button>
            </ResponsiveDesign.Desktop>
            <Button
              size={isOnboardingRedesign ? 'lg' : 'xl'}
              isDisabled
              endIcon={isOnboardingRedesign ? undefined : ArrowRight}
              aria-label="Looks Good"
              fullWidth={isMobile}
            >
              Looks Good
            </Button>
          </FooterButtons>
        </StickyFooter>
      }
    >
      <div className={Styles.container}>
        <BrandProfileTitleWithEditing
          name={brandProfile.name}
          canEdit={false}
          isEditing={false}
          onEdit={() => {}}
          onSave={() => {}}
          onCancel={() => {}}
          isSaving={false}
          loadingElement={loadingElement}
        />
        <div className={Styles.form}>
          <div ref={topRef} />
          {sections.length > 0 && (
            <div className={Styles.brandProfileContent}>
              {sections.map((section, index) => (
                <EditableSection
                  key={`${section.title}-${index}`}
                  section={section}
                  onSectionUpdate={handleSectionUpdate}
                  isUpdating={updateMutation.isLoading}
                  isEditing={editingSectionTitle === section.title}
                  onEdit={() => setEditingSectionTitle(section.title)}
                  onCancel={() => setEditingSectionTitle(null)}
                  isDisabled={editingSectionTitle !== null && editingSectionTitle !== section.title}
                  sectionClassName={Styles.section}
                  headerClassName={Styles.sectionHeader}
                  actionsClassName={Styles.sectionActions}
                  editButtonClassName={Styles.editButton}
                  contentClassName={Styles.sectionContent}
                  editingClassName={Styles.editingContainer}
                  editorClassName={Styles.proseEditor}
                />
              ))}
            </div>
          )}
          {(isLoading || profile.length < 1) && !isOnboardingRedesign && (
            <LoadingAnimation
              showSupportButton={!isMobile}
              handleBack={() => handleBack()}
              websiteUrl={brandProfile.url}
              cancelOnError={false}
              header={
                isMobile
                  ? 'Building your brand profile and strategy  🚀'
                  : 'Building a business overview and marketing strategy 🚀'
              }
            />
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </StickyFooterProvider>
  )
}

const BrandProfileWithEditingFormikContainer = ({ brandProfile, ...rest }: BrandProfileProps) => (
  <Formik onSubmit={() => {}} enableReinitialize initialValues={{}}>
    <BrandProfileWithEditing brandProfile={brandProfile} {...rest} />
  </Formik>
)

const BrandProfileTitleWithEditing = ({
  name,
  canEdit,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  isSaving,
  loadingElement,
}: {
  name?: string
  canEdit?: boolean
  isEditing?: boolean
  onEdit?: () => void
  onSave?: () => void
  onCancel?: () => void
  isSaving?: boolean
  loadingElement?: React.ReactNode
}) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()

  return (
    <div className={Styles.titleContainer}>
      {isOnboardingRedesign ? (
        <div className={Styles.brandProfileTitleContainer}>
          <Heading level={1}>{`${name ? makePossessiveSingular(name) : 'Building your'} Business Profile`}</Heading>
          {loadingElement}
        </div>
      ) : (
        <Heading level={1}>{`${name ? makePossessiveSingular(name) : 'Your'} Brand Profile ✨`}</Heading>
      )}
      {canEdit &&
        (isEditing ? (
          <div className={Styles.sectionActions}>
            <Button variant="editor" id="brand-profile-cancel" size="sm" onPress={onCancel} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button variant="ghost" id="brand-profile-save" size="sm" onPress={onSave} isDisabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" frontIcon={Edit1} onPress={onEdit}>
            Edit
          </Button>
        ))}
    </div>
  )
}

export { BrandProfileWithEditingFormikContainer as BrandProfileWithEditing }
